import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet, apiPatch } from '../lib/api';
import { formatCents, formatDate, formatTime } from '../lib/format';
import { useTenant } from '../lib/tenantContext';
import {
  IntakeFieldDisplay,
  IntakeFieldInput,
} from '../components/IntakeFieldInput';
import { SkeletonBar, SkeletonList } from '../components/Skeleton';
import { toast } from '../lib/toast';

interface Customer {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
  metadata_json: string;
  total_visits: number;
  last_visit_at: string | null;
  created_at: string;
}

interface HistoryRow {
  id: number;
  start_at: string;
  end_at: string;
  status: string;
  source: string;
  service_name: string;
  price_cents: number;
  operator_notes: string | null;
}

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const { tenant } = useTenant();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // edit-form state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [metadata, setMetadata] = useState<Record<string, unknown>>({});

  const customerFields = tenant?.customer_fields ?? [];

  const parsedMeta = useMemo<Record<string, unknown>>(() => {
    if (!customer) return {};
    try {
      return JSON.parse(customer.metadata_json || '{}');
    } catch {
      return {};
    }
  }, [customer]);

  async function load() {
    try {
      const r = await apiGet<{ customer: Customer; history: HistoryRow[] }>(
        `/api/customers/${id}`
      );
      setCustomer(r.customer);
      setHistory(r.history);
      let meta: Record<string, unknown> = {};
      try {
        meta = JSON.parse(r.customer.metadata_json || '{}');
      } catch {}
      setName(r.customer.name);
      setPhone(r.customer.phone || '');
      setEmail(r.customer.email || '');
      setNotes(r.customer.notes || '');
      setMetadata(meta);
    } catch (e) {
      setErr((e as Error).message);
    }
  }
  useEffect(() => {
    load();
  }, [id]);

  async function save() {
    setErr(null);
    setBusy(true);
    try {
      // Strip empty strings/undefined so we don't litter metadata_json with junk.
      const cleaned: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(metadata)) {
        if (v === undefined || v === null || v === '') continue;
        cleaned[k] = v;
      }
      await apiPatch(`/api/customers/${id}`, {
        name,
        phone,
        email,
        notes,
        metadata: cleaned,
      });
      setEditing(false);
      toast.success('Customer saved.');
      load();
    } catch (e) {
      const msg = (e as Error).message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  if (err) return <div className="text-rose-400">{err}</div>;
  if (!customer) {
    return (
      <div className="space-y-4">
        <Link to="/customers" className="text-sm text-muted">
          ← Customers
        </Link>
        <div className="card space-y-3">
          <SkeletonBar className="h-6 w-1/2" />
          <SkeletonBar className="h-4 w-1/3" />
          <SkeletonBar className="h-4 w-2/5" />
        </div>
        <SkeletonList count={3} variant="row" />
      </div>
    );
  }

  // Show only fields that have a value AND fields that are in the schema.
  // (We render even unknown extras at the bottom in case a tenant changed
  // templates and old metadata is still meaningful.)
  const knownKeys = new Set(customerFields.map((f) => f.key));
  const extras = Object.entries(parsedMeta).filter(
    ([k, v]) => !knownKeys.has(k) && v !== '' && v !== null && v !== undefined
  );

  return (
    <div className="space-y-4">
      <Link to="/customers" className="text-sm text-muted">
        ← Customers
      </Link>

      <div className="card space-y-3">
        {!editing ? (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-xl font-bold truncate">{customer.name}</h1>
                {customer.phone && (
                  <a href={`tel:${customer.phone}`} className="text-accent block font-mono">
                    {customer.phone}
                  </a>
                )}
                {customer.email && (
                  <a
                    href={`mailto:${customer.email}`}
                    className="text-muted block truncate"
                  >
                    {customer.email}
                  </a>
                )}
              </div>
              <button
                onClick={() => setEditing(true)}
                className="btn-ghost px-3 py-2 text-sm shrink-0"
              >
                Edit
              </button>
            </div>

            {(customerFields.some((f) => parsedMeta[f.key] !== undefined && parsedMeta[f.key] !== '') ||
              extras.length > 0) && (
              <div className="bg-panel-2 rounded p-2 text-sm space-y-1">
                {customerFields.map((f) => (
                  <IntakeFieldDisplay
                    key={f.key}
                    field={f}
                    value={parsedMeta[f.key]}
                  />
                ))}
                {extras.map(([k, v]) => (
                  <div key={k} className="flex items-baseline gap-2">
                    <span className="text-muted text-xs uppercase tracking-wide">
                      {k}
                    </span>
                    <span className="text-text/90">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}

            {customer.notes && (
              <div className="bg-panel-2 rounded p-2 text-sm">
                📝 {customer.notes}
              </div>
            )}

            <div className="flex items-center justify-between text-sm text-muted border-t border-border pt-2">
              <span>
                <span className="font-mono">{customer.total_visits}</span> visit
                {customer.total_visits === 1 ? '' : 's'}
              </span>
              {customer.last_visit_at && (
                <span>last: {formatDate(customer.last_visit_at)}</span>
              )}
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="label">Name</label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Phone</label>
              <input
                className="input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Email</label>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {customerFields.map((f) => (
              <IntakeFieldInput
                key={f.key}
                field={f}
                value={metadata[f.key]}
                onChange={(v) =>
                  setMetadata((prev) => ({ ...prev, [f.key]: v }))
                }
              />
            ))}

            <div>
              <label className="label">Notes</label>
              <textarea
                className="input"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setEditing(false)} className="btn-ghost">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={busy}
                className="btn-primary disabled:opacity-40"
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>

      <div>
        <h2 className="font-semibold mb-2">History</h2>
        {history.length === 0 ? (
          <p className="text-muted text-sm">No bookings yet.</p>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="card text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold">{formatDate(h.start_at)}</span>
                  <span className="text-muted font-mono">{formatTime(h.start_at)}</span>
                </div>
                <div className="text-text/90">
                  {h.service_name} · <span className="font-mono">{formatCents(h.price_cents)}</span>
                </div>
                <div className="text-xs text-muted mt-0.5">
                  <span className="capitalize">{h.status.replace('_', ' ')}</span>
                  <span> · </span>
                  <span className="capitalize">{h.source.replace('_', ' ')}</span>
                </div>
                {h.operator_notes && (
                  <p className="text-xs text-text/90 mt-1">📝 {h.operator_notes}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
